/**
 * CSV 내보내기 유틸리티 (V47)
 *
 * 브라우저에서 CSV 파일 생성 및 다운로드
 */

import { formatDateTimeByCompanyCode } from './dateUtils';  // V62: 시차 정책 적용

/**
 * 데이터 배열을 CSV 문자열로 변환
 *
 * @param data 객체 배열
 * @param columns 컬럼 정의 (key: 객체 키, label: CSV 헤더)
 * @returns CSV 문자열
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[],
  columns: { key: keyof T; label: string }[]
): string {
  // BOM (Byte Order Mark) for Excel 한글 인식
  const BOM = '\uFEFF';

  // 헤더 행
  const header = columns.map(col => escapeCSVValue(col.label)).join(',');

  // 데이터 행
  const rows = data.map(item =>
    columns.map(col => escapeCSVValue(String(item[col.key] ?? ''))).join(',')
  );

  return BOM + [header, ...rows].join('\n');
}

/**
 * CSV 값 이스케이프 처리
 * - 쉼표, 줄바꿈, 따옴표가 포함된 경우 따옴표로 감싸기
 * - 따옴표는 두 개로 이스케이프
 */
function escapeCSVValue(value: string): string {
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * CSV 문자열을 파일로 다운로드
 *
 * @param csvContent CSV 문자열
 * @param filename 파일명 (확장자 포함)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // 정리
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 고용관리 히스토리 CSV 내보내기
 *
 * @param logs 히스토리 로그 배열
 * @param filename 파일명 (기본: employment_history_YYYYMMDD.csv)
 */
export function exportEmploymentHistoryToCSV(
  logs: Array<{
    executedAt: string;
    actionTypeName: string;
    employeeName: string;
    employeeNumber?: string;
    email?: string;
    effectiveDate: string;
    executedByName: string;
    companyCode?: string;  // V62: 시차 정책 적용
  }>,
  filename?: string
): void {
  const columns = [
    { key: 'executedAt' as const, label: '실행일시' },
    { key: 'actionTypeName' as const, label: '액션' },
    { key: 'employeeName' as const, label: '직원명' },
    { key: 'employeeNumber' as const, label: '사번' },
    { key: 'email' as const, label: '이메일' },
    { key: 'effectiveDate' as const, label: '적용일' },
    { key: 'executedByName' as const, label: '실행자' },
  ];

  // V62: 실행일시를 companyCode 기준 시차로 포맷팅 (테이블 렌더링과 동일 정책)
  const formattedLogs = logs.map(log => ({
    ...log,
    executedAt: log.executedAt
      ? formatDateTimeByCompanyCode(log.executedAt, log.companyCode, true)
      : '',
  }));

  const csvContent = convertToCSV(formattedLogs, columns);

  // 기본 파일명 생성
  const defaultFilename = `employment_history_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;

  downloadCSV(csvContent, filename || defaultFilename);
}
